import { useInjection } from 'inversify-react';
import { useCallback, useState } from 'react';
import { Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { ImageCapture } from '../components/digiinsulin/ImageCapture';
import { API } from '../services/HTTP';

type PresignResponse = {
    url?: string;
    uploadUrl?: string;
    s3Key?: string;
};

type AnalyzeResponse = {
    SK?: string;
    id?: string;
    status?: string;
    error?: string;
    message?: string;
};

function parseResponseData<T>(data: unknown): T {
    if (typeof data === 'string') {
        return JSON.parse(data) as T;
    }

    return data as T;
}

function extractErrorMessage(error: unknown): string {
    if (typeof error === 'object' && error !== null) {
        if ('response' in error && typeof error.response === 'object' && error.response !== null && 'data' in error.response) {
            const responseData = (error.response as { data?: unknown }).data;

            if (typeof responseData === 'string') {
                try {
                    const parsed = JSON.parse(responseData) as { message?: string; error?: string };
                    return parsed.message || parsed.error || responseData;
                } catch {
                    return responseData;
                }
            }

            if (typeof responseData === 'object' && responseData !== null) {
                if ('message' in responseData && typeof responseData.message === 'string') {
                    return responseData.message;
                }

                if ('error' in responseData && typeof responseData.error === 'string') {
                    return responseData.error;
                }
            }
        }

        if ('message' in error && typeof error.message === 'string') {
            return error.message;
        }
    }

    return 'Unable to process the DigiInsulin image.';
}

export function DigiInsulinCapturePage() {
    const api: API = useInjection<API>('API');
    const navigate = useNavigate();
    const [error, setError] = useState<string | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);

    const handleImageSelected = useCallback(async (file: File) => {
        setError(null);
        setIsUploading(true);
        setUploadProgress(10);

        try {
            const presignResponse = await api.post('digiinsulin/presign', JSON.stringify({
                filename: file.name,
                fileName: file.name,
                contentType: file.type || 'application/octet-stream',
            }));
            const { url, uploadUrl, s3Key } = parseResponseData<PresignResponse>(presignResponse.data);
            const resolvedUploadUrl = url || uploadUrl;

            if (!resolvedUploadUrl || !s3Key) {
                throw new Error('The upload link could not be created.');
            }

            const uploadResponse = await fetch(resolvedUploadUrl, {
                method: 'PUT',
                body: file,
                headers: {
                    'Content-Type': file.type || 'application/octet-stream',
                },
            });

            if (!uploadResponse.ok) {
                throw new Error('Uploading the image to storage failed.');
            }

            setUploadProgress(30);

            setUploadProgress(60);
            const analyzeResponse = await api.post('digiinsulin/analyze', JSON.stringify({ s3Key, originalFilename: file.name }));
            const result = parseResponseData<AnalyzeResponse>(analyzeResponse.data);

            const resultId = (result.id || result.SK || '').replace(/^ANALYSIS#/, '');

            if (!resultId) {
                throw new Error('Analysis could not be started — no identifier was returned.');
            }

            setUploadProgress(100);
            navigate(`/digiinsulin/results/${encodeURIComponent(resultId)}`);
        } catch (err) {
            setError(extractErrorMessage(err));
            setUploadProgress(0);
        } finally {
            setIsUploading(false);
        }
    }, [api, navigate]);

    return (
        <>
            <h1 className="mb-0">Capture</h1>

            {error && (
                <Alert variant="danger" dismissible onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            <ImageCapture
                onImageSelected={handleImageSelected}
                isUploading={isUploading}
                uploadProgress={uploadProgress}
            />
        </>
    );
}
